import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { MainMenu } from '../MainMenu';
import { Footer } from '../Footer';
import { BackToTop } from '../BackToTop';
import { useAccessibilitySettings } from '../../hooks/useAccessibilitySettings';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { DonationTopbar } from '../DonationTopbar';

interface LayoutProps {
  children: React.ReactNode;
  fontSize: number;
  adjustFontSize: (delta: number) => void;
}

export function Layout({ children, fontSize, adjustFontSize }: LayoutProps) {
  const { theme, toggleTheme } = useAccessibilitySettings();
  const location = useLocation();
  const segments = location.pathname.split('/').filter(Boolean);
  const isHome = location.pathname === '/';

  // Friendly names for URL segments (with diacritics)
  const segmentLabels: Record<string, string> = {
    'aktualnosci': 'Aktualności',
    'o-nas': 'O nas',
    'przekaz-podatek': 'Przekaż podatek',
    'wplata-jednorazowa': 'Wpłata jednorazowa',
    'za-teczowym-mostem': 'Za Tęczowym mostem',
    'wirtualna-adopcja': 'Wirtualna adopcja',
    'dom-tymczasowy': 'Dom tymczasowy',
    'historie-adopcyjne': 'Historie adopcyjne',
    'zwierze': 'Zwierzę',
    'zwierzeta': 'Zwierzęta',
    'sprawozdania': 'Sprawozdania',
    'deklaracja-dostepnosci': 'Deklaracja dostępności',
    'polityka-prywatnosci': 'Polityka prywatności',
    'polityka-cookies': 'Polityka cookies',
    'mapa-strony': 'Mapa strony',
    'wolontariat': 'Wolontariat',
    'adoptuj': 'Adoptuj',
    'kontakt': 'Kontakt',
    'wsparcie': 'Wesprzyj nas',
  };

  const toTitle = (text: string) => text
    .split('-')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

  const crumbs = [
    { name: 'Strona główna', to: '/' },
    ...segments.map((seg, idx) => {
      const raw = decodeURIComponent(seg);
      const isPetDetailRoot = raw === 'zwierze';
      const name = isPetDetailRoot ? 'Zwierzęta' : (segmentLabels[raw] || toTitle(raw));
      const to = isPetDetailRoot
        ? '/zwierzeta'
        : '/' + segments.slice(0, idx + 1).join('/');
      return { name, to };
    })
  ];
  
  // Initialize keyboard shortcuts with memoized callbacks
  useKeyboardShortcuts({
    adjustFontSize,
    toggleTheme,
    currentTheme: theme
  });

  // Apply theme class and font size on initial render
  React.useEffect(() => {
    // This ensures the theme is applied immediately on page load
    // before the useAccessibilitySettings hook in other components runs
    const savedTheme = document.cookie
      .split('; ')
      .find(row => row.startsWith('znajdki_theme='))
      ?.split('=')[1];
      
    if (savedTheme) {
      document.documentElement.classList.remove('dark', 'high-contrast');
      if (savedTheme === 'dark') {
        document.documentElement.classList.add('dark');
      } else if (savedTheme === 'high-contrast') {
        document.documentElement.classList.add('high-contrast');
      }
    }
    
    // Apply font size from cookie
    const savedFontSize = document.cookie
      .split('; ')
      .find(row => row.startsWith('znajdki_font_size='))
      ?.split('=')[1];
      
    if (savedFontSize) {
      document.documentElement.style.fontSize = `${savedFontSize}%`;
    }
  }, []);

  return (
    <div
      className="min-h-screen bg-white dark:bg-gray-900 high-contrast:bg-black"
    >
      <div className="sticky top-0 left-0 right-0 z-40" role="banner">
        <DonationTopbar isHome={isHome} hideCostMessageOnHome />
        <MainMenu adjustFontSize={adjustFontSize} fontSize={fontSize} />
      </div>
      <main role="main">
        {/* Breadcrumbs for AAA 2.4.8 */}
        {!isHome && (
          <nav aria-label="Okruszki" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-5 sm:mb-2 bg-transparent">
            <ol className="flex items-center flex-wrap gap-2 text-sm text-accessible-gray-600 dark:text-white high-contrast:text-white">
              {crumbs.map((crumb, idx) => (
                <li key={crumb.to} className="flex items-center gap-2">
                  {idx < crumbs.length - 1 ? (
                    <Link to={crumb.to} className="underline">{crumb.name}</Link>
                  ) : (
                    <span aria-current="page">{crumb.name}</span>
                  )}
                  {idx < crumbs.length - 1 && <span aria-hidden="true">/</span>}
                </li>
              ))}
            </ol>
          </nav>
        )}
        {children}
      </main>
      <footer role="contentinfo">
        <Footer />
      </footer>
      <BackToTop />
    </div>
  );
}